import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, CardFooter, Table, Badge, Button, Row, Col } from 'reactstrap';
import Swal from 'sweetalert2';
import axios from 'axios';
import { getPlanById, isTrialValid, getTrialDaysRemaining, getSpecialtyFields } from 'config/subscriptionPlans';

const BRAND_ICONS = {
    visa: 'fab fa-cc-visa',
    mastercard: 'fab fa-cc-mastercard',
    amex: 'fab fa-cc-amex',
    discover: 'fab fa-cc-discover',
    diners: 'fab fa-cc-diners-club',
    jcb: 'fab fa-cc-jcb',
};

const SOURCE_LABELS = {
    'trial-auto-charge': { label: 'Auto-charge', color: 'info' },
    'quick-buy': { label: 'Saved Card', color: 'primary' },
    'quick-buy-saved-card': { label: 'Saved Card', color: 'primary' },
    'quick-buy-saved-card-3ds': { label: 'Saved Card', color: 'primary' },
    'manual-subscription-purchase': { label: 'Purchase', color: 'primary' },
};

const formatDate = (d) => {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return '—';
    }
};

const renderCard = (record) => {
    if (!record.cardLast4) return <span className="text-muted">—</span>;
    const iconClass = BRAND_ICONS[record.cardBrand] || 'far fa-credit-card';
    const exp = record.cardExpMonth && record.cardExpYear
        ? `${String(record.cardExpMonth).padStart(2, '0')}/${record.cardExpYear}`
        : null;
    return (
        <div className="d-flex align-items-center">
            <i className={`${iconClass} mr-2`} style={{ fontSize: '1.5rem' }} />
            <div style={{ lineHeight: 1.2 }}>
                <div>
                    <span className="text-uppercase font-weight-bold mr-1">{record.cardBrand || ''}</span>
                    <span className="text-muted">•••• {record.cardLast4}</span>
                </div>
                {exp && <small className="text-muted">Expires {exp}</small>}
            </div>
        </div>
    );
};

const getPlanName = (record) => {
    if (record.planId) {
        const plan = getPlanById(record.planId);
        if (plan) return plan.name;
    }
    if (record.months) return `${record.months} Month${record.months !== 1 ? 's' : ''} Access`;
    return record.planId || '—';
};

const getStatus = (user, fields) => {
    if (!user) return null;

    if (user[fields.lifetimeAccess] === true || user[fields.lifetimeAccess] === 'true') {
        return {
            label: 'Lifetime Access',
            sub: 'Full permanent access',
            icon: 'fas fa-infinity',
            color: 'success',
            gradient: 'bg-gradient-success',
        };
    }

    if (user[fields.ultimateAdvisingStudent] === true || user[fields.ultimateAdvisingStudent] === 'true') {
        return {
            label: 'Ultimate Advising Student',
            sub: 'Full lifetime access',
            icon: 'fas fa-star',
            color: 'success',
            gradient: 'bg-gradient-success',
        };
    }

    if (user[fields.subscriptionEndDate]) {
        const endDate = new Date(user[fields.subscriptionEndDate]);
        const startDate = user[fields.subscriptionStartDate] ? new Date(user[fields.subscriptionStartDate]) : null;
        const now = new Date();
        const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

        if (now < endDate) {
            const totalDays = startDate
                ? Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)))
                : daysRemaining;
            const percent = Math.min(100, Math.max(0, (daysRemaining / totalDays) * 100));
            const warning = daysRemaining <= 30;
            return {
                label: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`,
                sub: `Active subscription \u2022 Expires ${formatDate(endDate)}`,
                icon: 'fas fa-check-circle',
                color: warning ? 'warning' : 'success',
                gradient: warning ? 'bg-gradient-warning' : 'bg-gradient-success',
                percent,
            };
        }
        return {
            label: 'Subscription Expired',
            sub: `Ended ${formatDate(endDate)}`,
            icon: 'fas fa-exclamation-circle',
            color: 'danger',
            gradient: 'bg-gradient-danger',
        };
    }

    if (user[fields.trialStartDate]) {
        const trialStart = new Date(user[fields.trialStartDate]);
        const trialEnd = new Date(trialStart);
        trialEnd.setDate(trialEnd.getDate() + 14);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));

        if (now < trialEnd) {
            const percent = Math.min(100, Math.max(0, (daysRemaining / 14) * 100));
            const warning = daysRemaining <= 7;
            return {
                label: `Free Trial \u2022 ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`,
                sub: `Ends ${formatDate(trialEnd)}`,
                icon: 'fas fa-gift',
                color: warning ? 'warning' : 'info',
                gradient: warning ? 'bg-gradient-warning' : 'bg-gradient-info',
                percent,
            };
        }
        return {
            label: 'Trial Expired',
            sub: `Ended ${formatDate(trialEnd)}`,
            icon: 'fas fa-exclamation-circle',
            color: 'danger',
            gradient: 'bg-gradient-danger',
        };
    }

    return {
        label: 'No Active Plan',
        sub: 'No subscription or trial on record',
        icon: 'fas fa-lock',
        color: 'muted',
        gradient: 'bg-gradient-default',
    };
};

const AdminUserSubscription = ({ selectedUser, onRefresh, activeSpecialty = 'pmr' }) => {
    const fields = getSpecialtyFields(activeSpecialty);
    const [cancellingSub, setCancellingSub] = useState(false);
    const [cancellingAutoCharge, setCancellingAutoCharge] = useState(false);
    const [records, setRecords] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [subCancelled, setSubCancelled] = useState(false);
    const [autoChargeCancelled, setAutoChargeCancelled] = useState(false);
    const itemsPerPage = 5;

    useEffect(() => {
        if (!selectedUser?.handle) return;
        setLoadingRecords(true);
        setRecords([]);
        setCurrentPage(1);
        setSubCancelled(false);
        setAutoChargeCancelled(false);
        axios
            .post('/fetchPaymentRecord', { table: 'subscriptions', user: selectedUser.handle })
            .then((res) => {
                setRecords(res.data?.data || res.data || []);
            })
            .catch(() => {
                setRecords([]);
            })
            .finally(() => setLoadingRecords(false));
    }, [selectedUser?.handle]);

    if (!selectedUser) return null;

    const status = getStatus(selectedUser, fields);
    if (!status) return null;

    // Cancel subscription
    const handleCancelSubscription = async () => {
        const res = await Swal.fire({
            title: 'Cancel subscription?',
            text: `This will cancel the Stripe subscription for ${selectedUser.name || selectedUser.handle}. Their access will continue until the current period ends.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel subscription',
            cancelButtonText: 'Keep subscription',
            confirmButtonColor: '#f5365c',
        });
        if (!res.isConfirmed) return;

        setCancellingSub(true);
        try {
            await axios.post('/cancelSubscription', { subsId: selectedUser[fields.subsId] });
            setSubCancelled(true);
            if (onRefresh) onRefresh();
            Swal.fire({ title: 'Subscription Cancelled', text: 'Cancellation has been scheduled. Access continues until the current period ends.', icon: 'success', timer: 3000 });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to cancel subscription', 'error');
        } finally {
            setCancellingSub(false);
        }
    };

    // Cancel auto-charge
    const handleCancelAutoCharge = async () => {
        const res = await Swal.fire({
            title: 'Cancel auto-charge?',
            text: `The trial will still run, but ${selectedUser.name || selectedUser.handle}'s card will not be charged when it ends.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel auto-charge',
            cancelButtonText: 'Keep auto-charge',
            confirmButtonColor: '#f5365c',
        });
        if (!res.isConfirmed) return;

        setCancellingAutoCharge(true);
        try {
            await axios.post('/admin/cancelTrialAutoCharge', { handle: selectedUser.handle, specialty: activeSpecialty });
            setAutoChargeCancelled(true);
            if (onRefresh) onRefresh();
            Swal.fire({ title: 'Auto-Charge Cancelled', text: 'The trial will still run but no charge will occur.', icon: 'success', timer: 2500 });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message || 'Failed to cancel auto-charge', 'error');
        } finally {
            setCancellingAutoCharge(false);
        }
    };

    // Auto-charge section
    const showAutoCharge = selectedUser[fields.autoChargeStatus] === 'pending' && isTrialValid(selectedUser[fields.trialStartDate]) && !autoChargeCancelled;
    const plan = showAutoCharge ? getPlanById(selectedUser[fields.selectedPlanId]) : null;
    const daysLeft = showAutoCharge ? getTrialDaysRemaining(selectedUser[fields.trialStartDate]) : 0;
    const chargeDate = showAutoCharge ? (() => {
        const d = new Date(selectedUser[fields.trialStartDate]);
        d.setDate(d.getDate() + 14);
        return d;
    })() : null;

    // Billing history
    const sorted = [...(Array.isArray(records) ? records : [])].sort((a, b) => {
        const aDate = new Date(a.createdAt || a.subscriptionStartDate || 0).getTime();
        const bDate = new Date(b.createdAt || b.subscriptionStartDate || 0).getTime();
        return bDate - aDate;
    });
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRecords = sorted.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sorted.length / itemsPerPage);

    return (
        <>
            {/* Section A: Subscription Status */}
            <h6 className="heading-small text-muted mb-4">Subscription Status</h6>
            <Card className="shadow border-0 mb-4" style={{ overflow: 'hidden' }}>
                <div className={status.gradient} style={{ height: '4px' }} />
                <CardBody className="p-4">
                    <div className="d-flex align-items-center flex-wrap">
                        <div
                            className={`icon icon-shape ${status.gradient} text-white rounded-circle shadow mr-3`}
                            style={{ width: '64px', height: '64px', minWidth: '64px' }}
                        >
                            <i className={status.icon} style={{ fontSize: '1.75rem' }} />
                        </div>
                        <div className="flex-grow-1 mr-3" style={{ minWidth: 0 }}>
                            <h6
                                className="text-uppercase text-muted mb-1 font-weight-bold"
                                style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}
                            >
                                Subscription Status
                            </h6>
                            <h2 className={`mb-1 text-${status.color}`} style={{ lineHeight: 1.2 }}>
                                {status.label}
                            </h2>
                            <p className="text-muted mb-0 small">{status.sub}</p>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Section B: Subscription Actions */}
            {selectedUser[fields.subsId] && !subCancelled && (
                <Card className="shadow border-0 mb-4" style={{ overflow: 'hidden' }}>
                    <div className="bg-gradient-danger" style={{ height: '4px' }} />
                    <CardBody className="p-4">
                        <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <div>
                                <h6 className="text-uppercase text-muted mb-1 font-weight-bold" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                                    Active Stripe Subscription
                                </h6>
                                <p className="text-muted mb-0 small">
                                    Subscription ID: <code>{selectedUser[fields.subsId]}</code>
                                </p>
                            </div>
                            <Button color="danger" outline onClick={handleCancelSubscription} disabled={cancellingSub} className="mt-2 mt-sm-0">
                                {cancellingSub
                                    ? <><i className="fas fa-spinner fa-spin mr-1" /> Cancelling...</>
                                    : <><i className="fas fa-ban mr-1" /> Cancel Subscription</>}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            )}
            {subCancelled && (
                <Card className="shadow border-0 mb-4" style={{ overflow: 'hidden' }}>
                    <div className="bg-gradient-success" style={{ height: '4px' }} />
                    <CardBody className="p-4 text-center">
                        <i className="fas fa-check-circle text-success mr-2" />
                        <span className="font-weight-bold">Subscription cancellation scheduled.</span>
                        <span className="text-muted ml-1">Access continues until the current period ends.</span>
                    </CardBody>
                </Card>
            )}

            {showAutoCharge && (
                <Card className="shadow border-0 mb-4" style={{ overflow: 'hidden' }}>
                    <div className="bg-gradient-warning" style={{ height: '4px' }} />
                    <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-4">
                            <div
                                className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow mr-3"
                                style={{ width: '64px', height: '64px', minWidth: '64px' }}
                            >
                                <i className="fas fa-hourglass-half" style={{ fontSize: '1.75rem' }} />
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <h6 className="text-uppercase text-muted mb-1 font-weight-bold" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                                    Trial Auto-Charge Scheduled
                                </h6>
                                <h2 className="mb-1 text-warning" style={{ lineHeight: 1.2 }}>
                                    Charging in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                </h2>
                                <p className="text-muted mb-0 small">
                                    {plan ? `$${plan.price} will be charged on ${formatDate(chargeDate)}` : `Charges on ${formatDate(chargeDate)}`}
                                </p>
                            </div>
                        </div>

                        <Row className="mb-4">
                            <Col xs="6" lg="3" className="mb-3 mb-lg-0">
                                <div className="p-3 border rounded h-100">
                                    <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                        <i className="fas fa-box mr-1" /> Plan
                                    </small>
                                    <span className="font-weight-bold">{plan ? plan.name : 'No plan selected'}</span>
                                </div>
                            </Col>
                            <Col xs="6" lg="3" className="mb-3 mb-lg-0">
                                <div className="p-3 border rounded h-100">
                                    <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                        <i className="fas fa-dollar-sign mr-1" /> Amount
                                    </small>
                                    <span className="font-weight-bold text-success">{plan ? `$${plan.price}` : '—'}</span>
                                </div>
                            </Col>
                            <Col xs="6" lg="3" className="mb-3 mb-lg-0">
                                <div className="p-3 border rounded h-100">
                                    <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                        <i className="fas fa-credit-card mr-1" /> Card
                                    </small>
                                    <span className="font-weight-bold text-muted">•••• {selectedUser.cardLast4 || '—'}</span>
                                </div>
                            </Col>
                            <Col xs="6" lg="3">
                                <div className="p-3 border rounded h-100">
                                    <small className="text-uppercase text-muted font-weight-bold d-block mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                        <i className="fas fa-calendar-alt mr-1" /> Charge Date
                                    </small>
                                    <span className="font-weight-bold">{formatDate(chargeDate)}</span>
                                </div>
                            </Col>
                        </Row>

                        <Button color="danger" outline onClick={handleCancelAutoCharge} disabled={cancellingAutoCharge}>
                            {cancellingAutoCharge
                                ? <><i className="fas fa-spinner fa-spin mr-1" /> Cancelling...</>
                                : <><i className="fas fa-ban mr-1" /> Cancel Auto-Charge</>}
                        </Button>
                    </CardBody>
                </Card>
            )}
            {autoChargeCancelled && (
                <Card className="shadow border-0 mb-4" style={{ overflow: 'hidden' }}>
                    <div className="bg-gradient-success" style={{ height: '4px' }} />
                    <CardBody className="p-4 text-center">
                        <i className="fas fa-check-circle text-success mr-2" />
                        <span className="font-weight-bold">Auto-charge cancelled.</span>
                        <span className="text-muted ml-1">The trial will still run but no charge will occur.</span>
                    </CardBody>
                </Card>
            )}

            {/* Section C: Billing History */}
            <h6 className="heading-small text-muted mb-4">Billing History</h6>
            <Card className="shadow">
                <CardHeader className="border-0">
                    <h3 className="mb-0">Payment Records</h3>
                </CardHeader>
                <CardBody className="pt-0">
                    {loadingRecords ? (
                        <div className="text-center py-5">
                            <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                            <p className="text-muted">Loading billing history...</p>
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="far fa-file-alt fa-4x text-muted mb-3"></i>
                            <h3 className="text-muted">No Billing History</h3>
                            <p className="text-muted mb-0">No subscription payments found for this user.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table className="align-items-center table-flush" hover>
                                <thead className="thead-light">
                                    <tr>
                                        <th scope="col">Date</th>
                                        <th scope="col">Plan</th>
                                        <th scope="col">Amount</th>
                                        <th scope="col">Card</th>
                                        <th scope="col">Access Until</th>
                                        <th scope="col">Source</th>
                                        <th scope="col">Invoice</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRecords.map((r, idx) => {
                                        const source = SOURCE_LABELS[r.source] || { label: 'Purchase', color: 'primary' };
                                        return (
                                            <tr key={r.docid || r.paymentId || idx}>
                                                <td>{formatDate(r.createdAt || r.subscriptionStartDate)}</td>
                                                <td>{getPlanName(r)}</td>
                                                <td className="font-weight-bold text-success">
                                                    ${Number(r.amount || 0).toFixed(2)}
                                                </td>
                                                <td>{renderCard(r)}</td>
                                                <td>{formatDate(r.subscriptionEndDate)}</td>
                                                <td>
                                                    <Badge color={source.color} pill>
                                                        {source.label}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const viewUrl = r.invoiceUrl || r.receiptUrl;
                                                        if (!viewUrl) return <span className="text-muted">—</span>;
                                                        return (
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                outline
                                                                tag="a"
                                                                href={viewUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <i className="fas fa-external-link-alt mr-1" />
                                                                View
                                                            </Button>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </CardBody>
                {!loadingRecords && sorted.length > 0 && (
                    <CardFooter className="py-4">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                            <div className="text-muted small mb-2 mb-sm-0">
                                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sorted.length)} of {sorted.length} record{sorted.length !== 1 ? 's' : ''}
                            </div>
                            <div className="d-flex align-items-center">
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => p - 1)}
                                    disabled={currentPage === 1 || totalPages <= 1}
                                    className="mr-2"
                                >
                                    <i className="fas fa-chevron-left"></i>
                                    <span className="d-none d-sm-inline ml-1">Previous</span>
                                </Button>
                                <span className="mx-2 text-nowrap">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => p + 1)}
                                    disabled={currentPage === totalPages || totalPages <= 1}
                                    className="ml-2"
                                >
                                    <span className="d-none d-sm-inline mr-1">Next</span>
                                    <i className="fas fa-chevron-right"></i>
                                </Button>
                            </div>
                        </div>
                    </CardFooter>
                )}
            </Card>
        </>
    );
};

export default AdminUserSubscription;
